import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useControlledState } from '@react-stately/utils';
import { useResizeObserver } from '@direct-frontend/stdlib';

import { cn } from '../../../lib/css/className/className';
import './GalleryAutoWidth.css';
import { HStack, VStack } from '../../Stack';
import { useDrag } from '../helpers/useDrag';
import { useMoveBackward } from '../helpers/useMoveBackward';
import { useMoveForward } from '../helpers/useMoveForward';
import { useMoveToCurrentSlide } from '../helpers/useMoveToCurrentSlide';
import { useCursor } from '../helpers/useCursor';
import { OverlayButtons } from '../OverlayButtons/OverlayButtons';
import { GalleryAutoWidthProps, ItemProps } from '../types';
import { useItemsToScrollForward } from '../helpers/useItemsToScrollForward';
import { useItemsToScrollBackward } from '../helpers/useItemsToScrollBackward';

import { useFindLastSwipeSlide, useFindActualSlideIndex } from './helpers';

const cls = cn('GalleryAutoWidth');

/**
 * Компонент для переключения слайдов с помощью кнопок или метода dragAndDrop
 * Слайды растягиваются по ширине контента
 * Компонент имеет кнопки по бокам слайдера и пагинацию
 */
export const GalleryAutoWidth = function GalleryAutoWidth<ItemPropsT extends ItemProps>({
    'data-testid': dataTestId = 'GalleryAutoWidth',
    className,
    arrowButtons,
    items,
    itemGapSize = '8',
    renderItemContent,
    animationDuration = 300,
    listClassName,
    currentSlide,
    onSwipe,
    itemsToScroll = 1,
    hasDraggedRef,
    getSwipeCount,
}: GalleryAutoWidthProps<ItemPropsT>) {
    const listContainerRef = useRef<HTMLDivElement>(null);
    const slides = useRef<Map<number, HTMLDivElement>>(new Map());

    // Храним количество элементов, чтобы сравнить изменение их количества
    const prevItemsCount = useRef(0);

    const [translateX, setTranslateX] = useState(0);
    const [currentSlideIndex, setCurrentSlideIndex] = useControlledState(currentSlide, 0, onSwipe);
    const [isDragging, setIsDragging] = useState(false);
    const [lastSwipeSlideIndex, setLastSwipeSlideIndex] = useState(0);
    const [listWidth, setListWidth] = useState(0);
    const findLastSwipeSlideHandler = useFindLastSwipeSlide();

    // eslint-disable-next-line react-hooks/refs -- Автоматический игнор в рамках DIRECT-288723
    const findActualSlideHandler = useFindActualSlideIndex(slides.current);
    const moveToCurrentSlideHandler = useMoveToCurrentSlide();
    const onResize = useCallback(() => {
        if (listContainerRef.current) {
            setListWidth(listContainerRef.current?.offsetWidth);
        }

        findLastSwipeSlideHandler({
            setLastSwipeSlideIndex,
            listContainerRef,
            slides,
            items,
        });

        /* при ресайзе проверяем явлется ли нынешний элемент последний из доступных для свайпа */
        if (lastSwipeSlideIndex < currentSlideIndex) {
            setCurrentSlideIndex(lastSwipeSlideIndex);
        }
    }, [items, findLastSwipeSlideHandler, lastSwipeSlideIndex, currentSlideIndex, setCurrentSlideIndex]);
    const resizeObserver = useResizeObserver(onResize);

    useEffect(() => {
        // Количество элементов не изменилось
        if (items.length === prevItemsCount.current) {
            return;
        }

        findActualSlideHandler({
            slides,
            currentSlideIndex,
            setCurrentSlideIndex,
        });

        findLastSwipeSlideHandler({
            setLastSwipeSlideIndex,
            listContainerRef,
            slides,
            items,
        });

        moveToCurrentSlideHandler({
            slides,
            currentSlideIndex,
            isDragging,
            listContainerRef,
            setTranslateX,
        });

        prevItemsCount.current = items.length;
    }, [
        items,
        findLastSwipeSlideHandler,
        findActualSlideHandler,
        moveToCurrentSlideHandler,
        setCurrentSlideIndex,
        currentSlideIndex,
        lastSwipeSlideIndex,
        isDragging,
    ]);

    useEffect(() => {
        const slidesIds = Array.from(slides.current.keys());

        // Если текущий элемент находится после максимально возможного, то сбрасываем
        if (slidesIds.indexOf(lastSwipeSlideIndex) < slidesIds.indexOf(currentSlideIndex)) {
            setCurrentSlideIndex(lastSwipeSlideIndex);
        }
    }, [lastSwipeSlideIndex, currentSlideIndex, setCurrentSlideIndex]);

    useEffect(() => {
        // Следит за изменением ширины контейнера
        if (resizeObserver && listContainerRef.current) {
            resizeObserver.observe(listContainerRef.current);
        }
    }, [resizeObserver]);

    useEffect(() => {
        moveToCurrentSlideHandler({
            slides,
            currentSlideIndex,
            isDragging,
            listContainerRef,
            setTranslateX,
        });
    }, [currentSlideIndex, isDragging, listWidth, moveToCurrentSlideHandler]);

    const itemsToScrollForward = useItemsToScrollForward({
        currentSlideIndex,
        itemsLength: items.length,
        itemsToScroll,
    });

    const moveForward = useMoveForward({
        currentSlideIndex,
        lastSwipeSlideIndex,
        setCurrentSlideIndex,
        itemsToScroll: itemsToScrollForward,
    });

    const itemsToScrollBackward = useItemsToScrollBackward({
        currentSlideIndex,
        itemsToScroll,
    });

    const moveBackward = useMoveBackward({
        currentSlideIndex,
        setCurrentSlideIndex,
        itemsToScroll: itemsToScrollBackward,
    });

    const forwardButtonProps = useMemo(() => {
        const isVisibleLastSlides = lastSwipeSlideIndex === currentSlideIndex;
        const isLastSlide = currentSlideIndex === items.length - 1;

        return {
            canMoveForward: !isLastSlide && !isVisibleLastSlides,
            onClick: moveForward,
        };
    }, [lastSwipeSlideIndex, currentSlideIndex, items, moveForward]);

    const backwardButtonProps = useMemo(
        () => ({
            canMoveBackward: currentSlideIndex !== 0,
            onClick: moveBackward,
        }),
        [currentSlideIndex, moveBackward],
    );

    const bindDrag = useDrag({
        setIsDragging,
        canMoveBackward: backwardButtonProps.canMoveBackward,
        canMoveForward: forwardButtonProps.canMoveForward,
        moveBackward,
        moveForward,
        hasDraggedRef,
        getSwipeCount,
        gestureProps: {
            setTranslateX,
            translateX,
            setCurrentSlideIndex,
            slides,
            lastSwipeSlideIndex,
        },
    });

    const cursorStyle = useCursor({
        isDragging,
        canMoveBackward: backwardButtonProps.canMoveBackward,
        canMoveForward: forwardButtonProps.canMoveForward,
    });

    const listStyle = useMemo(
        () => ({
            gap: `${itemGapSize}px`,
            transform: `translate3d(${translateX}px, 0, 0)`,
            transition: `${isDragging ? 0 : animationDuration}ms`,
            cursor: cursorStyle,
        }),
        [itemGapSize, translateX, isDragging, animationDuration, cursorStyle],
    );

    useEffect(() => {
        if (slides.current.size > items.length) {
            slides.current.clear();
        }
    }, [items.length]);

    return (
        <div data-testid={dataTestId} className={cls('', [className])}>
            <VStack>
                <div ref={listContainerRef} className={cls('listContainer')}>
                    <div {...bindDrag()} className={cls('list', [listClassName])} style={listStyle}>
                        {/* Слайды */}
                        {items.map((item, index) => (
                            <div
                                key={item.id}
                                data-item-id={item.id}
                                ref={el => {
                                    if (el) {
                                        slides.current.set(index, el);
                                    } else {
                                        slides.current.delete(index);
                                    }
                                }}
                            >
                                {renderItemContent(item)}
                            </div>
                        ))}
                    </div>
                </div>
                <HStack alignItems="center" justifyContent="flex-end" gap="8" className={cls('buttonsContainer')}>
                    <OverlayButtons
                        className={arrowButtons?.className}
                        color={arrowButtons?.color}
                        size={arrowButtons?.size}
                        onClick={arrowButtons?.onClick}
                        onClickBackward={backwardButtonProps.onClick}
                        onClickForward={forwardButtonProps.onClick}
                        isHiddenBackward={arrowButtons?.isHidden || !backwardButtonProps.canMoveBackward}
                        isHiddenForward={arrowButtons?.isHidden || !forwardButtonProps.canMoveForward}
                    />
                </HStack>
            </VStack>
        </div>
    );
};
